import React, { useState, useRef, useEffect } from "react";
import { CheckCircle } from "lucide-react";

const PackagesSection = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const packages = [
    {
      name: "Starter",
      price: "$2,500",
      subtitle: "Perfect for small business",
      features: [
        "Basic responsive website",
        "Basic SEO on-site",
        "Contact for integrations",
        "30 days support",
      ],
      buttonText: "Get Started",
    },
    {
      name: "Professional",
      price: "$5,000",
      subtitle: "For growing business",
      features: [
        "10-page responsive website",
        "Advanced SEO Optimization",
        "Custom functionality",
        "CMS Integration",
        "90 days support",
      ],
      buttonText: "Get Started",
    },
    {
      name: "Enterprise",
      price: "Custom",
      subtitle: "For large organizations",
      features: [
        "Unlimited Pages",
        "Enterprise SEO strategy",
        "Custom development",
        "Dedicated account manager",
        "24/7 priority support",
      ],
      buttonText: "Contact Us",
    },
  ];

  const handlePackageClick = (packageName) => {
    const message = `Hi, I'm interested in the ${packageName} package. Can you provide more details?`;
    const whatsappUrl = `https://wa.me/918472916169?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  // Handle scroll snap for mobile
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.offsetWidth;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndex(newIndex);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-blue-500 font-semibold mb-2">PRICING</p>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Package
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            No matter if your goal is to organize, strengthen your personal
            life, or monetize including or even focus—offer a tailored plan.
          </p>
        </div>

        {/* Desktop View */}
        <div className="hidden md:flex justify-center items-stretch gap-6 mb-8">
          {packages.map((pkg, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`relative flex-1 max-w-xs rounded-2xl p-8 transition-all duration-300 cursor-pointer flex flex-col ${
                hoveredIndex === index
                  ? "bg-blue-500 text-white scale-105 shadow-2xl z-10"
                  : "bg-white scale-95 opacity-75"
              }`}
            >
              <div className="text-center mb-6">
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    hoveredIndex === index ? "text-white" : "text-gray-900"
                  }`}
                >
                  {pkg.name}
                </h3>
                <div
                  className={`text-4xl font-bold mb-2 ${
                    hoveredIndex === index ? "text-white" : "text-gray-900"
                  }`}
                >
                  {pkg.price}
                </div>
                <p
                  className={
                    hoveredIndex === index ? "text-blue-100" : "text-gray-600"
                  }
                >
                  {pkg.subtitle}
                </p>
              </div>

              <div className="space-y-4 mb-8 flex-grow">
                {pkg.features.map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-start gap-3">
                    <CheckCircle
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        hoveredIndex === index ? "text-white" : "text-blue-500"
                      }`}
                    />
                    <span
                      className={
                        hoveredIndex === index ? "text-white" : "text-gray-700"
                      }
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handlePackageClick(pkg.name)}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 mt-auto ${
                  hoveredIndex === index
                    ? "bg-white text-blue-500 hover:bg-gray-50"
                    : "border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
                }`}
              >
                {pkg.buttonText}
              </button>
            </div>
          ))}
        </div>

        {/* Mobile View - Snap Scroll */}
        <div className="md:hidden">
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-8"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <div className="flex gap-4 px-4">
              {packages.map((pkg, index) => (
                <div
                  key={index}
                  className={`flex-shrink-0 w-[calc(100vw-2rem)] max-w-sm snap-center rounded-2xl p-8 transition-all duration-300 flex flex-col ${
                    activeIndex === index
                      ? "bg-blue-500 text-white shadow-2xl scale-100"
                      : "bg-white scale-95 opacity-75"
                  }`}
                >
                  <div className="text-center mb-6">
                    <h3
                      className={`text-2xl font-bold mb-2 ${
                        activeIndex === index ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {pkg.name}
                    </h3>
                    <div
                      className={`text-4xl font-bold mb-2 ${
                        activeIndex === index ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {pkg.price}
                    </div>
                    <p
                      className={
                        activeIndex === index
                          ? "text-blue-100"
                          : "text-gray-600"
                      }
                    >
                      {pkg.subtitle}
                    </p>
                  </div>

                  <div className="space-y-4 mb-8 flex-grow">
                    {pkg.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start gap-3">
                        <CheckCircle
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            activeIndex === index
                              ? "text-white"
                              : "text-blue-500"
                          }`}
                        />
                        <span
                          className={
                            activeIndex === index
                              ? "text-white"
                              : "text-gray-700"
                          }
                        >
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePackageClick(pkg.name)}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 mt-auto ${
                      activeIndex === index
                        ? "bg-white text-blue-500 hover:bg-gray-50"
                        : "border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
                    }`}
                  >
                    {pkg.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Scroll Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {packages.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  const container = scrollContainerRef.current;
                  if (container) {
                    container.scrollTo({
                      left: index * container.offsetWidth,
                      behavior: "smooth",
                    });
                  }
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  activeIndex === index ? "bg-blue-500 w-8" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default PackagesSection;
